import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CEO_CHAT_STORE_KEY, type PermissionKey, type RoleKey, type SessionCover, type SessionDesk } from '@bright/shared';
import {
  ApiError,
  api,
  getAccessToken,
  setAccessToken,
  setRefreshHandler,
  setBookShutHandler,
  setSessionExpiredHandler,
} from './api';
import { booksFor, coverInUse, getActiveBook, setActiveBook, useActiveBook } from './book';
import { accessSubject, renewedElsewhere } from './token-sync';

/**
 * Session state.
 *
 * Two tokens: a short-lived access token that rides on every request, and a refresh
 * token that buys a new one. Both are persisted so a reload does not sign an operator
 * out mid-shift; the API client calls back into `refresh()` here whenever it meets a
 * 401, which is the only place a token is ever renewed.
 *
 * Permissions are a flat list of keys. There is no client-side role logic — a role is
 * a bag of permissions on the server, an administrator can re-cut them from the roles
 * screen, and the UI only ever asks "may this user do X".
 */

const ACCESS_KEY = 'bsa.accessToken';
const REFRESH_KEY = 'bsa.refreshToken';

export interface AuthUser {
  id: string;
  email: string;
  username?: string | null;
  name: string;
  organizationId: string;
  role: { id?: string; key: RoleKey; name: string };
  asmId: string | null;
  isActive?: boolean;
  /** Head office: this one sign-in may open every book from the header. */
  allBooks?: boolean;
  /** Places orders for the company outlets only (30 Sep 2026) — Surbhi. */
  ordersForOutlets?: boolean;
  /** Places orders for any counter in the book (1 Oct 2026) — Jitendra. */
  ordersForAll?: boolean;
  /** Enrols employees for an administrator's approval (7 Oct 2026) — Abhishek. */
  enrolsEmployees?: boolean;
  /** Approves a mitr's vehicle transfer before the receiver is asked (13 Oct 2026) — Surbhi, and Abhishek in her absence. */
  approvesTransfers?: boolean;
  /** Checks the counter's balance on every order, beside its ASM (23 Oct 2026) — Abhishek. */
  checksOrderBalance?: boolean;
  /** A guard who sends and receives material (24 Oct 2026) — Akhilesh. */
  movesMaterial?: boolean;
  /** Keeps the attendance register without being a head-admin desk (14 Oct 2026) — Surbhi. */
  attendanceAdmin?: boolean;
  /** Reconciles the bank statement against the expense sheet (19 Oct 2026). */
  reconcilesExpenses?: boolean;
  lastLoginAt?: string | null;
  /** The party a scoped account is pinned to — an outlet, a wholesale buyer. */
  /** `fullSaleForm`: an outlet that sells on the mitr's full form (29 Sep 2026). */
  scopeParty?: { id: string; name: string; type: string; fullSaleForm?: boolean } | null;
  /** wholesale=true marks the OEM book — its staff land on the sales board. */
  organization?: { id: string; name: string; slug: string; wholesale: boolean };
  /** Another person's work held while they are away (20 Oct 2026) — see `coverInUse`. */
  cover?: SessionCover | null;
  /** One person's desks (20 Oct 2026) — Vivek's store and dispatch sign-ins; the header switches between them. */
  desks?: SessionDesk[];
  /** This sign-in is another desk of that person: attendance, tasks and chat live on theirs. */
  seatOf?: { id: string; name: string } | null;
}

export interface Session {
  user: AuthUser;
  permissions: PermissionKey[];
}

interface AuthPayload {
  accessToken: string;
  refreshToken?: string;
  user: AuthUser;
  permissions?: PermissionKey[];
}

interface SessionPayload {
  user: AuthUser & { permissions?: PermissionKey[] };
  permissions?: PermissionKey[];
}

interface AuthContextValue {
  user: AuthUser | null;
  permissions: PermissionKey[];
  /** True until the boot-time session check has settled. Guards must wait for this. */
  isLoading: boolean;
  /** Start-up could not reach the server; it keeps trying with the tokens kept. */
  unreachable: boolean;
  /** A start-up retry is in flight. */
  checking: boolean;
  /** Start-up: try the server again now rather than at the next tick. */
  retryNow: () => void;
  /** Start-up: stop waiting for the server and go to the sign-in screen. */
  signInAgain: () => void;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  /** The login screen's portal cards: a portal key and a four-digit PIN. */
  loginWithPin: (portal: string, pin: string) => Promise<AuthUser>;
  /** Everyone's PIN door: the account chosen by name, then its PIN. */
  loginWithUserPin: (userId: string, pin: string) => Promise<AuthUser>;
  /** Another desk of the same person (20 Oct 2026): no PIN, this device's session moves over. */
  switchDesk: (userId: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Re-reads the current user — call after a role or profile change. */
  reload: () => Promise<void>;
  has: (permission: PermissionKey) => boolean;
  hasAny: (permissions: PermissionKey[]) => boolean;
  hasAll: (permissions: PermissionKey[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the session lives for this tab only. Still usable.
  }
}

/** Accepts either `{ user, permissions }` or a user object carrying its own list. */
/**
 * A book chosen in an earlier session (the header switch) that this sign-in
 * may not open is forgotten, never sent — the Autozone head admin must not
 * inherit a Bright on Wheels choice from the same browser (8 Sep 2026).
 */
function settleBook(user: AuthUser | null): void {
  const active = getActiveBook();
  if (active && !booksFor(user).includes(active)) setActiveBook(null);
}

/** The server said no to the session itself — not "busy", not "updating", not "unreachable". */
function sessionRefused(err: ApiError): boolean {
  return err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
}

/** What a session check came to: settled either way, or the server could not be reached. */
type SessionOutcome = 'settled' | 'unreachable';

/** The server could not be reached or was busy — nothing said about the session itself. */
function unreachableError(err: unknown): boolean {
  return err instanceof ApiError && err.isRetryable;
}

/** Each start-up retry after the first patient one, while the server stays away. */
const BOOT_RETRY_MS = 15_000;

/**
 * At start-up, ride out an update or a busy minute instead of showing the
 * sign-in screen (9 Oct 2026): an update takes the API away for up to a
 * minute, and a person opening the app then was sent to sign in again.
 * Retries only what is retryable, for about 45 seconds, behind the loading
 * screen.
 */
async function patiently<T>(attempt: () => Promise<T>): Promise<T> {
  const waits = [2000, 4000, 8000, 15000, 15000];
  for (let i = 0; ; i++) {
    try {
      return await attempt();
    } catch (err) {
      if (!(err instanceof ApiError) || !err.isRetryable || i >= waits.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, waits[i]));
    }
  }
}

function normaliseSession(payload: SessionPayload): Session {
  const { permissions: onUser, ...user } = payload.user ?? ({} as SessionPayload['user']);
  return {
    user: user as AuthUser,
    permissions: payload.permissions ?? onUser ?? [],
  };
}

/** The CEO's conversation (26 Oct 2026) is the signed-in person's: it goes when the session does. */
function forgetCeoChat(): void {
  try {
    window.sessionStorage.removeItem(CEO_CHAT_STORE_KEY);
  } catch {
    // Storage is blocked; there is nothing kept to forget.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  /** Takes a session on, first dropping any book choice it may not use. */
  const adopt = useCallback((next: Session) => {
    settleBook(next.user);
    setSession(next);
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [checking, setChecking] = useState(false);
  const bootControls = useRef<{ retry: () => void; stop: () => void; done: () => void }>({
    retry: () => undefined,
    stop: () => undefined,
    done: () => undefined,
  });
  const refreshToken = useRef<string | null>(readStored(REFRESH_KEY));

  const applyTokens = useCallback((access: string | null, refresh?: string | null) => {
    setAccessToken(access);
    writeStored(ACCESS_KEY, access);
    if (refresh !== undefined) {
      refreshToken.current = refresh;
      writeStored(REFRESH_KEY, refresh);
    }
  }, []);

  const clearSession = useCallback(() => {
    applyTokens(null, null);
    setSession(null);
    queryClient.clear();
    forgetCeoChat();
  }, [applyTokens, queryClient]);

  /**
   * Exchanges the refresh token for a new access token. Returns null when there is
   * nothing to exchange or the server refuses — the API client reads that as
   * "the session is over" and stops retrying.
   */
  const refresh = useCallback(async (): Promise<string | null> => {
    const failed = getAccessToken();
    // One renewal at a time across every tab of this browser, each reading the
    // token from storage once it has the turn (9 Oct 2026): a tab renewing with
    // the token another tab already spent closed every session of the account.
    const renew = async (): Promise<string | null> => {
      const stored = readStored(REFRESH_KEY);
      const storedAccess = readStored(ACCESS_KEY);
      if (stored && renewedElsewhere(failed, storedAccess)) {
        refreshToken.current = stored;
        setAccessToken(storedAccess);
        return storedAccess;
      }
      const token = stored ?? refreshToken.current;
      if (!token) return null;
      try {
        const payload = await api.post<AuthPayload>(
          '/auth/refresh',
          { refreshToken: token },
          { anonymous: true },
        );
        if (!payload?.accessToken) return null;
        applyTokens(payload.accessToken, payload.refreshToken ?? token);
        if (payload.user) {
          adopt({ user: payload.user, permissions: payload.permissions ?? [] });
        }
        return payload.accessToken;
      } catch (err) {
        // Only the server refusing the token ends the session (9 Oct 2026). A 429
        // — the office's shared sign-in budget — a 5xx while an update swaps the
        // containers, or a dropped connection is not the end of anything: until
        // then every one of them signed the person out, and the sign-ins that
        // followed from one office address ran into the 429 themselves. Keep the
        // tokens and let the request fail as retryable.
        if (err instanceof ApiError && sessionRefused(err)) {
          applyTokens(null, null);
          return null;
        }
        throw err;
      }
    };
    return 'locks' in navigator ? navigator.locks.request('bsa.refresh', renew) : renew();
  }, [applyTokens, adopt]);

  // Wire the client to this provider once, before anything can fire a request.
  // Set during render rather than in an effect so the first query in a child
  // component already carries the bearer header.
  const wired = useRef(false);
  if (!wired.current) {
    wired.current = true;
    setAccessToken(readStored(ACCESS_KEY));
  }

  useEffect(() => {
    setRefreshHandler(refresh);
    setSessionExpiredHandler(() => {
      setSession(null);
      applyTokens(null, null);
    });
    return () => {
      setRefreshHandler(null);
      setSessionExpiredHandler(null);
    };
  }, [refresh, applyTokens]);

  /**
   * Reads the stored session. 'unreachable' when the server could not be
   * reached or was busy: the tokens are kept and the session left as it was,
   * because nothing has been said about it yet (9 Oct 2026 — until then the
   * person was shown the sign-in screen, and had to sign in again).
   */
  const loadSession = useCallback(async (patient = true): Promise<SessionOutcome> => {
    const persist = patient ? patiently : <T,>(attempt: () => Promise<T>) => attempt();
    const access = readStored(ACCESS_KEY);
    const stored = readStored(REFRESH_KEY);
    refreshToken.current = stored;
    setAccessToken(access);

    if (!access && !stored) {
      setSession(null);
      return 'settled';
    }

    // With no access token but a refresh token in hand (a long-closed tab), take the
    // refresh first so `/auth/me` is not a guaranteed 401.
    if (!access && stored) {
      let renewed: string | null;
      try {
        renewed = await persist(refresh);
      } catch (err) {
        if (unreachableError(err)) return 'unreachable';
        setSession(null);
        return 'settled';
      }
      if (!renewed) {
        clearSession();
        return 'settled';
      }
    }

    try {
      const payload = await persist(() => api.get<SessionPayload>('/auth/me', undefined, { book: null }));
      adopt(normaliseSession(payload));
      return 'settled';
    } catch (err) {
      if (err instanceof ApiError && (err.isAuthFailure || err.isForbidden)) {
        clearSession();
        return 'settled';
      }
      if (unreachableError(err)) return 'unreachable';
      // Broken in a way retrying will not mend. Keep the tokens, but report as
      // unauthenticated so guards send them somewhere honest.
      setSession(null);
      return 'settled';
    }
  }, [refresh, clearSession, adopt]);

  // Boot: resolve the stored session. While the server cannot be reached the
  // loading screen stays up, says so, and keeps trying — every fifteen seconds,
  // when the connection comes back and when the app is brought back to the front.
  useEffect(() => {
    let cancelled = false;
    let running = false;
    let timer: number | undefined;
    const onOnline = () => void run();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void run();
    };
    const detach = () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
    const finish = () => {
      detach();
      setUnreachable(false);
      setChecking(false);
      setIsLoading(false);
    };
    let first = true;
    async function run(): Promise<void> {
      if (cancelled || running) return;
      running = true;
      window.clearTimeout(timer);
      if (!first) setChecking(true);
      // The first look is patient (about 45 seconds behind the plain loading
      // screen); after that each look is one try.
      const outcome = await loadSession(first);
      first = false;
      running = false;
      if (cancelled) return;
      setChecking(false);
      if (outcome === 'settled') {
        finish();
        return;
      }
      setUnreachable(true);
      timer = window.setTimeout(() => void run(), BOOT_RETRY_MS);
    }
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    bootControls.current = {
      retry: () => void run(),
      // The person chose the sign-in screen: the tokens stay, the session is not shown.
      stop: () => {
        cancelled = true;
        setSession(null);
        finish();
      },
      // Signed in from the sign-in screen while start-up was still waiting.
      done: () => {
        if (cancelled) return;
        cancelled = true;
        finish();
      },
    };
    void run();
    return () => {
      cancelled = true;
      detach();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryNow = useCallback(() => bootControls.current.retry(), []);
  const signInAgain = useCallback(() => bootControls.current.stop(), []);

  // Signing out in one tab signs out the others; a renewal in one tab is taken
  // up by the others, so none of them presents a token already spent.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_KEY) return;
      if (event.newValue === null) {
        setAccessToken(null);
        refreshToken.current = null;
        setSession(null);
        queryClient.clear();
        forgetCeoChat();
        return;
      }
      const access = readStored(ACCESS_KEY);
      if (!session || !access || accessSubject(access) !== session.user.id) {
        // Signed in in another tab for the first time, or as somebody else.
        if (session) queryClient.clear();
        void loadSession();
        return;
      }
      refreshToken.current = event.newValue;
      setAccessToken(access);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [session, loadSession, queryClient]);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthUser> => {
      const payload = await api.post<AuthPayload>(
        '/auth/login',
        { email: email.trim().toLowerCase(), password },
        { anonymous: true },
      );
      if (!payload?.accessToken || !payload.user) {
        throw new ApiError({
          code: 'INTERNAL',
          message: 'Sign in did not complete. Please try again.',
          status: 500,
        });
      }
      applyTokens(payload.accessToken, payload.refreshToken ?? null);
      queryClient.clear();

      if (payload.permissions) {
        adopt({ user: payload.user, permissions: payload.permissions });
      } else {
        // The login response did not carry permissions; ask for them explicitly
        // rather than guessing from the role key.
        const me = await api.get<SessionPayload>('/auth/me', undefined, { book: null });
        adopt(normaliseSession(me));
      }
      bootControls.current.done();
      return payload.user;
    },
    [applyTokens, queryClient, adopt],
  );

  const loginWithPin = useCallback(
    async (portal: string, pin: string): Promise<AuthUser> => {
      const payload = await api.post<AuthPayload>(
        '/auth/pin-login',
        { portal, pin },
        { anonymous: true },
      );
      if (!payload?.accessToken || !payload.user) {
        throw new ApiError({
          code: 'INTERNAL',
          message: 'Sign in did not complete. Please try again.',
          status: 500,
        });
      }
      applyTokens(payload.accessToken, payload.refreshToken ?? null);
      queryClient.clear();
      if (payload.permissions) {
        adopt({ user: payload.user, permissions: payload.permissions });
      } else {
        adopt(normaliseSession({ user: payload.user }));
      }
      bootControls.current.done();
      return payload.user;
    },
    [applyTokens, queryClient, adopt],
  );

  const loginWithUserPin = useCallback(
    async (userId: string, pin: string): Promise<AuthUser> => {
      const payload = await api.post<AuthPayload>(
        '/auth/pin-login',
        { userId, pin },
        { anonymous: true },
      );
      if (!payload?.accessToken || !payload.user) {
        throw new ApiError({
          code: 'INTERNAL',
          message: 'Sign in did not complete. Please try again.',
          status: 500,
        });
      }
      applyTokens(payload.accessToken, payload.refreshToken ?? null);
      queryClient.clear();
      if (payload.permissions) {
        adopt({ user: payload.user, permissions: payload.permissions });
      } else {
        adopt(normaliseSession({ user: payload.user }));
      }
      bootControls.current.done();
      return payload.user;
    },
    [applyTokens, queryClient, adopt],
  );

  /*
   * Another desk of the same person (owner, 20 Oct 2026: "merge them this is
   * the same person"): Vivek's store desk in Bright on Wheels and his dispatch
   * desk in Autozone. No PIN — he is signed in already; the server ends this
   * desk's session and hands back the other's, which this device then holds.
   */
  const switchDesk = useCallback(
    async (userId: string): Promise<AuthUser> => {
      const token = readStored(REFRESH_KEY) ?? refreshToken.current;
      const payload = await api.post<AuthPayload>('/auth/switch-desk', token ? { userId, refreshToken: token } : { userId }, { book: null });
      if (!payload?.accessToken || !payload.user) {
        throw new ApiError({
          code: 'INTERNAL',
          message: 'The switch did not complete. Please try again.',
          status: 500,
        });
      }
      // The other desk works in its own book: the header starts from that desk's home book.
      setActiveBook(null);
      applyTokens(payload.accessToken, payload.refreshToken ?? null);
      queryClient.clear();
      if (payload.permissions) {
        adopt({ user: payload.user, permissions: payload.permissions });
      } else {
        adopt(normaliseSession({ user: payload.user }));
      }
      return payload.user;
    },
    [applyTokens, queryClient, adopt],
  );

  const logout = useCallback(async (): Promise<void> => {
    const token = readStored(REFRESH_KEY) ?? refreshToken.current;
    try {
      await api.post('/auth/logout', token ? { refreshToken: token } : undefined);
    } catch {
      // The token may already be revoked or expired. Local state is what matters.
    } finally {
      setActiveBook(null);
      clearSession();
    }
  }, [clearSession]);

  /*
   * A cover started or ended by the owner (20 Oct 2026) reaches a session
   * already open: the session is read again when the app comes back to the
   * front (at most once a minute), every five minutes while it is open, and at
   * once when the server shuts a book the screens still had open. Quietly — a
   * failed look leaves the session exactly as it was; only a fresh answer for
   * the person still signed in is taken (review, 20 Oct 2026), and a book that
   * answer closes takes its data with it.
   */
  const sessionUserId = session?.user?.id ?? null;
  useEffect(() => {
    if (!sessionUserId) return;
    let alive = true;
    let last = Date.now();
    const look = (force: boolean) => {
      if (document.visibilityState !== 'visible' && !force) return;
      if (!force && Date.now() - last < 60_000) return;
      last = Date.now();
      const bookBefore = getActiveBook();
      api
        .get<SessionPayload>('/auth/me', undefined, { book: null })
        .then((payload) => {
          const next = normaliseSession(payload);
          if (!alive || next.user.id !== sessionUserId) return;
          adopt(next);
          if (getActiveBook() !== bookBefore) void queryClient.invalidateQueries();
        })
        .catch(() => undefined);
    };
    const onVisible = () => look(false);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') look(true);
    }, 5 * 60_000);
    document.addEventListener('visibilitychange', onVisible);
    setBookShutHandler(() => {
      void queryClient.invalidateQueries();
      look(true);
    });
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      setBookShutHandler(null);
    };
  }, [sessionUserId, adopt, queryClient]);

  const reload = useCallback(async (): Promise<void> => {
    // An unreachable server leaves the session as it was.
    await loadSession();
  }, [loadSession]);

  /*
   * In the book of a person whose work this session covers (20 Oct 2026), the
   * screens ask of that work: only it, where the cover is the only way in, or
   * one's own and it together — as the server answers there.
   */
  const activeBook = useActiveBook();
  const permissions = useMemo<PermissionKey[]>(() => {
    const own = session?.permissions ?? [];
    const cover = coverInUse(session?.user, activeBook);
    if (!cover) return own;
    const covered = cover.permissions as PermissionKey[];
    return cover.crossBook ? covered : [...new Set([...own, ...covered])];
  }, [session, activeBook]);

  const permissionSet = useMemo(() => new Set<string>(permissions), [permissions]);

  const has = useCallback(
    (permission: PermissionKey) => permissionSet.has(permission),
    [permissionSet],
  );
  const hasAny = useCallback(
    (keys: PermissionKey[]) => keys.some((key) => permissionSet.has(key)),
    [permissionSet],
  );
  const hasAll = useCallback(
    (keys: PermissionKey[]) => keys.every((key) => permissionSet.has(key)),
    [permissionSet],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      permissions,
      isLoading,
      unreachable,
      checking,
      retryNow,
      signInAgain,
      isAuthenticated: Boolean(session?.user),
      login,
      loginWithPin,
      loginWithUserPin,
      switchDesk,
      logout,
      reload,
      has,
      hasAny,
      hasAll,
    }),
    [session, permissions, isLoading, unreachable, checking, retryNow, signInAgain, login, switchDesk, logout, reload, has, hasAny, hasAll],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}

/** Convenience for conditionally rendering an action the user may not hold. */
export function usePermission(permission: PermissionKey): boolean {
  return useAuth().has(permission);
}
